import React, { useState } from "react"
import { Button } from "react-bootstrap"
import { defineMessages, FormattedMessage, injectIntl } from "react-intl"
import { connect, ConnectedProps } from "react-redux"
import { DOCUMENT_TYPE } from "../../../state/Constants"
import { FeeDto } from "../../../state/fee/Fee"
import { Intl } from "../../../state/helpers/IntlProps"
import { INVOICE } from "../../../state/invoice/Constants"
import { ORDER } from "../../../state/order/Constants"
import OrderService from "../../../state/order/OrderService"
import { PRICE_LIST } from "../../../state/price-list/Constants"
import PriceListService from "../../../state/price-list/PriceListService"
import Service from "../../../state/pricing/services/service"
import RowCustomStatus from "../../../state/pricing/rowcustomstatus/RowCustomStatus"
import { getPricingTableItemFees, getPricingTableTaxByBillingMethod } from "../../../state/pricing/selectors"
import { BILLING_METHOD } from "../../../state/product/Types"
import { QUOTE } from "../../../state/quote/Constants"
import QuoteService from "../../../state/quote/QuoteService"
import { TaxDto } from "../../../state/tax/Tax"
import ActionButton from "../../button/actionbutton.component"
import Icon from "../../icon/icon.component"
import ItemBulkTaxQuoteComponent from "./ItemBulkTaxQuoteComponent"
import ItemBulkUpdateAssignedUser from "./ItemBulkUpdateAssignedUser"
import ItemBulkUpdateRowCustomStatusModal from "./ItemBulkUpdateRowCustomStatusModal"
import { InvoiceService } from "../../../state/invoice/services"
import { PRICING_TABLE } from "../../../utils/pricing/constants"
import PricingTableRowService from "../../../state/pricing/PricingTableRowService"

const uuidv4 = require("uuid/v4")

type OwnProps = {
    objectId: string,
    itemIdList: Set<string>,
    rowCustomStatuses: RowCustomStatus[]
    editable: boolean,
    assignedToUserId: string,
    title: string,
    documentType: DOCUMENT_TYPE,
    taxable: boolean,
    fees: FeeDto[],
    tax: TaxDto,
    taxId: string,
    onChange: () => void,
    intl: Intl,
    tableId: string,
    containsBundleItems: boolean,
}

type Payload = {
    status?: string
    taxable?: boolean
    measureEditable?:boolean
    feeApplicable?: boolean
    rowCustomStatusId?: string
    rowIds?: string[]
    toDelete?: boolean
}

const messages = defineMessages({
    "bulk_save_type_error": {
        id: "quote.items.bulk_save_type_error",
        defaultMessage: "\"Invalid object type. Cannot save.\"",
    },
    "bundle_measure_editable_save_error": {
        id: "quote.component.item_bulk_update_component.bundle_editable_quantity_save_error",
        defaultMessage: "Cannot set editable quantity for bundle items.",
    },
})
const uniqueId = uuidv4()

const ItemBulkUpdateComponent = (props: Props) => {
    const [state, setState] = useState({
        editMode: false,
        itemStatus: "active",
        taxable: true,
        measureEditable : false,
        feeApplicable: props.fees && props.fees.length > 0 ? true : false,
        setModal: <></>,
        saveSuccess: false,
        toDelete: false,
    })
    const [errorMsg, setErrorMsg] = useState<string>("")

    const triggerEdit = (event) => {
        event?.preventDefault()
        setErrorMsg("")
        setState({
            ...state,
            editMode: !state.editMode
        })
    }

    const handleClickButton = (name: string, value) => {
        return function (event) {
            event.preventDefault()
            setState({
                ...state,
                [name]: value,
            })
        }.bind(this)
    }

    const openSetTaxModal = (event) => {
        setState({
            ...state,
            setModal: <ItemBulkTaxQuoteComponent
                taxId={props.taxId}
                tax={props.tax}
                objectId={props.objectId}
                mainObject={props.documentType}
                itemIdList={props.itemIdList}
                billingMethod={"one-time"}
                onClose={closeSetTaxModal}
                mainTaxesOnly={false}
                intl={messages} />
        })
        event.preventDefault()
    }

    const closeSetTaxModal = () => {
        setState({
            ...state,
            setModal: <></>
        })
        props.onChange()
        setState({
            ...state,
            editMode: !state.editMode
        })
    }


    const openSetAssignedUserModal = (event) => {
        setState({
            ...state,
            setModal: <ItemBulkUpdateAssignedUser
                assignedToUserId={props.assignedToUserId}
                objectId={props.objectId}
                mainObject={props.documentType}
                itemIdList={props.itemIdList}
                onUpdateCallback={props.onChange || function () { return }}
                onClose={closeSetAssignedUserModal} />
        })
        event.preventDefault()
    }

    const openSetRowCustomStatusModal = () => {
        setState({
            ...state,
            setModal: <ItemBulkUpdateRowCustomStatusModal
                show={true}
                rowCustomStatuses={props.rowCustomStatuses}
                onSaveCallback={payload => onSave(payload)}
                onCloseCallback={() => closeSetRowCustomStatusModal()} />
        })
    }

    const closeSetRowCustomStatusModal = () => {
        setState({
            ...state,
            setModal: <></>
        })
    }
    const closeSetAssignedUserModal = () => {
        setState({
            ...state,
            setModal: <></>
        })
        props.onChange()
        setState({
            ...state,
            editMode: !state.editMode
        })
    }

    const isQuote = () => {
        return ["quote"].includes(props.documentType)
    }

    const isOrder = () => {
        return ["order"].includes(props.documentType)
    }

    const isPriceList = () => {
        return ["price-list"].includes(props.documentType)
    }

    const pricingTableSupportOptionalOptions = () => {
        return isQuote() || isPriceList()
    }

    const onSave = (payload: Payload) => {
        setErrorMsg("")

        payload = {
            ...payload,
            rowIds: Array.from(props.itemIdList.values()),
        }

        if (props.containsBundleItems && state.measureEditable) {
            setErrorMsg(props.intl.formatMessage(messages["bundle_measure_editable_save_error"]))
            return
        }

        let res
        if (state.toDelete) {
            res = Service.deleteRows(props.tableId, Array.from(props.itemIdList))
        } else {
            switch (props.documentType) {
            case QUOTE:
                res = QuoteService.applyBulkUpdate(props.objectId, payload)
                break
            case INVOICE:
                res = InvoiceService.applyBulkUpdate(props.objectId, payload)
                break
            case ORDER:
                res = OrderService.applyBulkUpdate(props.objectId, payload)
                break
            case PRICE_LIST:
                res = PriceListService.applyBulkUpdate(props.objectId, payload)
                break
            case PRICING_TABLE:
                res = PricingTableRowService.applyBulkUpdate(props.objectId,payload)
                break
            default:
                setErrorMsg(props.intl.formatMessage(messages["bulk_save_type_error"]))
                return
            }
        }

        res.then((r: { error: { message: string } }) => {
            if (!r.error) {
                setState({
                    ...state, saveSuccess: true
                })
                setTimeout(() => {
                    setState({
                        ...state,
                        editMode: false,
                        saveSuccess: false,
                        toDelete: false,
                    })
                    props.onChange()
                }, 1000)
            } else {
                setErrorMsg(r.error.message)
            }
        })
    }

    const isPricingTable = props.documentType === PRICING_TABLE
    return (
        <span className={"ml-2"}>
            <button title={props.title} id={uniqueId} type="button" className="btn btn-sm btn-outline-primary"
                aria-haspopup="false" aria-expanded="false" onClick={triggerEdit}>
                <Icon value={"ellipsis-h"} className={"px-1"} />
            </button>
            <div className={"dropdown-menu p-2 " + (state.editMode ? "show" : "")} style={{ top: "auto" }}>
                {!isPricingTable &&
                <button className={"dropdown-item btn-sm py-0"} type={"button"}
                    onClick={handleClickButton("itemStatus", "active")} disabled={state.toDelete}>
                    <input type="radio"
                        id={"itemStatusActive" + uniqueId}
                        name="itemStatus"
                        value="active"
                        checked={state.itemStatus === "active"}
                        className="align-middle"
                        onClick={(event) => {
                            setState({
                                ...state,
                                itemStatus: "active",
                            })
                            event.stopPropagation()
                        }}
                    />
                    <label className="ml-2 my-1" htmlFor={"itemStatusActive" + uniqueId}>
                        <FormattedMessage
                            id="quote.add_item_modal.configure.item_status.active_label"
                            defaultMessage="Active"
                        />
                    </label>
                </button>
                }
                {pricingTableSupportOptionalOptions() &&
                    <div>
                        <button className="dropdown-item btn-sm py-0" type="button"
                            onClick={handleClickButton("itemStatus", "option-for-customer")} disabled={state.toDelete}>
                            <input type="radio"
                                id={"itemStatusOptionForCustomer" + uniqueId}
                                name="itemStatus"
                                value="option-for-customer"
                                checked={state.itemStatus === "option-for-customer"}
                                className="align-middle"
                                onClick={(event) => {
                                    setState({
                                        ...state,
                                        itemStatus: "option-for-customer",
                                    })
                                    event.stopPropagation()
                                }}
                            />
                            <label className="ml-2 my-1" htmlFor={"itemStatusOptionForCustomer" + uniqueId}>
                                <FormattedMessage
                                    id="quote.add_item_modal.configure.item_status.optional_for_customer_label"
                                    defaultMessage="Optional Item"
                                />
                            </label>
                        </button>
                        
                        <button className="dropdown-item btn-sm py-0" type="button"
                            onClick={handleClickButton("itemStatus", "multiple-choice")} disabled={state.toDelete}>
                            <input type="radio"
                                id={"itemStatusMultipleChoice" + uniqueId}
                                name="itemStatus"
                                value="multiple-choice"
                                checked={state.itemStatus === "multiple-choice"}
                                className="align-middle"
                                onClick={(event) => {
                                    setState({
                                        ...state,
                                        itemStatus: "multiple-choice",
                                    })
                                    event.stopPropagation()
                                }}
                            />
                            <label className="ml-2 my-1" htmlFor={"itemStatusMultipleChoice" + uniqueId}>
                                <FormattedMessage
                                    id="quote.add_item_modal.configure.item_status.multiple_choice_label"
                                    defaultMessage="Multiple Choice"
                                />
                            </label>
                        </button>
                    </div>
                }
                {isQuote() &&
                    <button className="dropdown-item btn-sm py-0" type="button"
                        onClick={handleClickButton("itemStatus", "provided-by-customer")} disabled={state.toDelete}>
                        <input type="radio"
                            id={"itemStatusProvidedByCustomer" + uniqueId}
                            name="itemStatus"
                            value="provided-by-customer"
                            checked={state.itemStatus === "provided-by-customer"}
                            className="align-middle"
                            onClick={(event) => {
                                setState({
                                    ...state,
                                    itemStatus: "provided-by-customer",
                                })
                                event.stopPropagation()
                            }}
                        />
                        <label className="ml-2  my-1" htmlFor={"itemStatusProvidedByCustomer" + uniqueId}>
                            <FormattedMessage
                                id="quote.add_item_modal.configure.item_status.provided_by_customer_label"
                                defaultMessage="Provided by customer"
                            />
                        </label>
                    </button>
                }
                {!isPricingTable && 
                <>
                    <button className="dropdown-item btn-sm py-0" type="button"
                        onClick={handleClickButton("itemStatus", "included")} disabled={state.toDelete}>
                        <input type="radio"
                            id={"itemStatusIncluded" + uniqueId}
                            name="itemStatus"
                            value="included"
                            checked={state.itemStatus === "included"}
                            className="align-middle"
                            onClick={(event) => {
                                setState({
                                    ...state,
                                    itemStatus: "included",
                                })
                                event.stopPropagation()
                            }}/>
                        <label className="ml-2 my-1" htmlFor={"itemStatusIncluded" + uniqueId}>
                            <FormattedMessage
                                id="quote.add_item_modal.configure.item_status.included_label"
                                defaultMessage="Included"/>
                        </label>
                    </button>
                    <div className="dropdown-divider"/>
                </>
                }
                <button className="dropdown-item btn-sm" type="button"
                    onClick={handleClickButton("taxable", !state.taxable)} disabled={state.toDelete}>
                    <input type="checkbox"
                        id={"taxable" + uniqueId}
                        name="taxable"
                        checked={state.taxable}
                        className="align-middle"
                        onClick={(event) => {
                            setState({
                                ...state,
                                taxable: !state.taxable,
                            })
                            event.stopPropagation()
                        }}/>
                    <label className="ml-2 my-1" htmlFor={"taxable" + uniqueId}>
                        <FormattedMessage
                            id="quote.add_item_modal.taxable_label"
                            defaultMessage="Taxable"/>
                    </label>
                </button>
                {pricingTableSupportOptionalOptions() && <button className="dropdown-item btn-sm" type="button"
                    onClick={handleClickButton("measureEditable", !state.measureEditable)} disabled={state.toDelete}>
                    <input type="checkbox"
                        key={`${state.measureEditable}`}
                        id={"measureEditable" + uniqueId}
                        name="measureEditable"
                        checked={state.measureEditable}
                        className="align-middle"
                        onChange={(event) => {
                            setState({
                                ...state,
                                measureEditable: !state.measureEditable,
                            })
                            event.stopPropagation()
                        }}
                    />
                    <label className="ml-2 my-1" htmlFor={"measureEditable" +uniqueId}>
                        <FormattedMessage
                            id="quote.add_item_modal.configure.item_status.editable_quantity_label"
                            defaultMessage="Editable quantity"
                        />
                    </label>
                </button>}
                {props.fees && props.fees.length > 0 &&
                    <button className="dropdown-item btn-sm" type="button"
                        onClick={handleClickButton("feeApplicable", !state.feeApplicable)} disabled={state.toDelete}>
                        <input type="checkbox"
                            id={"feeApplicable" + uniqueId}
                            name="feeApplicable"
                            checked={!state.feeApplicable}
                            className="align-middle"
                            onClick={(event) => {
                                setState({
                                    ...state,
                                    feeApplicable: !state.feeApplicable,
                                })
                                event.stopPropagation()
                            }}
                        />
                        <label className="ml-2 my-1" htmlFor={"feeApplicable" + uniqueId}>
                            <FormattedMessage
                                id="quote.item.taxable.fees_exempt_label"
                                defaultMessage="Fee Exempt"
                            />
                        </label>
                    </button>
                }
                {(isOrder() || isPricingTable) &&
                    <>
                        <div className="dropdown-divider " />

                        <button className="dropdown-item btn-sm py-2" type="button"
                            onClick={openSetAssignedUserModal} disabled={state.toDelete}>
                            <label className={"mb-0 d-flex align-item-center"}>
                                <Icon value={"edit"} className={"mr-1"} />
                                <span>
                                    <FormattedMessage
                                        id="quote.add_item_modal.configure.item_status.order_row_user"
                                        defaultMessage="Assigned User"
                                    />
                                </span>
                            </label>
                        </button>

                        {props.rowCustomStatuses && props.rowCustomStatuses.length>0 && !isPricingTable &&
                        <>
                            <div className="dropdown-divider " />
                            <button className="dropdown-item btn-sm py-2" type="button"
                                onClick={openSetRowCustomStatusModal} disabled={state.toDelete}>
                                <label className={"mb-0 d-flex align-item-center"}>
                                    <Icon value={"edit"} className={"mr-1"} />
                                    <span>
                                        <FormattedMessage
                                            id="quote.item_bulk_update_component.row_custom_status_label"
                                            defaultMessage="Row Status"
                                        />
                                    </span>
                                </label>
                            </button>
                        </>}
                    </>
                }
                <>
                    <div className="dropdown-divider "/>
                    <button className="dropdown-item btn-sm py-2" type="button" onClick={openSetTaxModal}
                        disabled={state.toDelete}>
                        <label className={"mb-0 d-flex align-item-center"}>
                            <Icon value={"edit"} className={"mr-1"}/>
                            <span>
                                <FormattedMessage
                                    id="quote.add_item_modal.configure.item_status.sales_tax_label"
                                    defaultMessage="Sales Tax"/>
                            </span>
                        </label>
                    </button>
                    <div className="dropdown-divider"/>
                </>
                
                <button className="dropdown-item btn-sm py-2" type="button" 
                    onClick={handleClickButton("toDelete", !state.toDelete)}>
                    <label className={"mb-0 d-flex align-item-center " + (state.toDelete ? "text-danger" : "")}>
                        <Icon value={"trash"} className={"mr-1"} />
                        <span>
                            <FormattedMessage
                                id="quote.item_bulk_update_component.delete_rows_label"
                                defaultMessage="Delete Rows"
                            />
                        </span>
                    </label>
                </button>

                <p className="ml-3 pt-2 pb-0">
                    <button title={"Cancel update"} className="btn btn-sm btn-outline-secondary" onClick={triggerEdit}>
                        <Icon value={"arrow-left"} />
                    </button>
                    {!state.saveSuccess ?
                        <ActionButton
                            title={"Save update"}
                            loadingTimeout={1000}
                            className="btn btn-sm btn-outline-primary ml-2"
                            onClick={() => onSave({
                                status: state.itemStatus,
                                taxable: state.taxable,
                                measureEditable: state.measureEditable,
                                feeApplicable: state.feeApplicable,
                            })}>
                            <Icon value={"save"} />
                        </ActionButton>
                        :
                        <Button variant={"outline-success"} className="btn btn-sm ml-2" disabled>
                            <Icon value={"check"} className="text-success" />
                        </Button>
                    }
                </p>

                <div className="mx-3 pb-3 text-danger small text-wrap">{errorMsg}</div>
            </div>
            {state.setModal}
        </span>
    )
}


const mapState = (state, props: OwnProps) => {
    const objectId = props.objectId
    const tax = getPricingTableTaxByBillingMethod(state, BILLING_METHOD.ONE_TIME, objectId)
    return {
        ...props,
        tax: tax,
        fees: getPricingTableItemFees(state, objectId)
    }
}

const mapDispatch = {}
const connector = connect(mapState, mapDispatch)
type Props = ConnectedProps<typeof connector>
export default connector(injectIntl(ItemBulkUpdateComponent))


// WEBPACK FOOTER //
// ./src/common/quote/component/ItemBulkUpdateComponent.tsx